import React, { useEffect, useState } from 'react'
import { Box, ButtonBase, Link, Tooltip } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import FileNameText from 'src/components/FileNameText'
import { PATH_DASHBOARD } from 'src/routes/paths'
import { KindIcon, SegmentedBar, StepLanes } from 'src/views/Batches/BatchParts'
import { batchPath, formatDuration, formatWhen, isSomaticRow, pluralise } from 'src/views/Batches/useBatches'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import HomeCard, { CardMessage, RowSkeletons, listSx, metaSx, rowSx } from './HomeCard'
import { byCardWidth } from './layout'
import {
  currentStepOf,
  formatRunning,
  inProgressCountText,
  inProgressRows,
  lastFinishedText,
  rowSampleLabel,
  shortRowType,
} from './model'
import useInProgress from './useInProgress'

// ----------------------------------------------------------------------
// Home's "In progress" card: unfinished batches first, then running analyses
// that are not part of a batch, at most MAX_ROWS rows.
//
//   [dna] WES run 41    ▓▓▓▓▓▓▓▓▓▓▓▓░░░░░░░░░░░░       31/48 done
//   [dna] GN-2290  WES  ■■□□□         Alignment               48m
//
// A batch opens its page. A single analysis is shown in Analysis History below
// (onReveal(row)); "+N more" scrolls there too (onShowHistory()).
// ----------------------------------------------------------------------

// Kind, name, type, phases, current phase, time. Where the card is narrow
// (phones, a third of the page until the page is wide, see ./layout) the type and
// the phase marks give way and the current phase keeps its words. From md up to
// 1280px the card has the page's full width, below Ready to analyse and Suggested.
// The edge columns are auto so they take in the rows' side padding (rows are
// subgrids).
const FULL_COLUMNS = 'auto minmax(104px, 1fr) auto minmax(96px, max-content) auto auto'
const COMPACT_COLUMNS = 'auto minmax(72px, 1fr) auto auto'
const COLUMNS_SX = byCardWidth({
  gridTemplateColumns: { base: { xs: COMPACT_COLUMNS, sm: FULL_COLUMNS }, three: COMPACT_COLUMNS, roomy: FULL_COLUMNS },
})
const FULL_ONLY_SX = byCardWidth({ display: { base: { xs: 'none', sm: 'flex' }, three: 'none', roomy: 'flex' } })
// A batch's bar takes the type and phase columns, its count the last two
// (only the last where the card is narrow).
const BAR_SX = byCardWidth({ gridColumn: { base: { xs: '3 / -2', sm: '3 / -3' }, three: '3 / -2', roomy: '3 / -3' } })
const COUNT_SX = byCardWidth({
  gridColumn: { base: { xs: '-2 / -1', sm: '-3 / -1' }, three: '-2 / -1', roomy: '-3 / -1' },
})

// Re-renders once a minute while something runs, so running times move, and
// when the page is shown again. Times are read from the clock at each render
// (not kept from the last tick), so a card that sat idle for hours is right as
// soon as new data arrives.
const useMinuteTick = (enabled) => {
  const [, setTick] = useState(0)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setTick((n) => n + 1)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
  useEffect(() => {
    if (!enabled) return undefined
    const id = window.setInterval(() => setTick((n) => n + 1), 60 * 1000)
    return () => window.clearInterval(id)
  }, [enabled])
}

const nameSx = {
  minWidth: 0,
  fontSize: 13.5,
  fontWeight: 500,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const BatchRow = ({ batch }) => {
  const theme = useTheme()
  const { done, total } = batch.counts
  return (
    <Box
      component={RouterLink}
      to={batchPath(batch.id)}
      data-row
      aria-label={`Batch ${batch.name}, ${done} of ${total} done`}
      sx={rowSx(theme, { interactive: true })}
    >
      <KindIcon kind={batch.kind} size={16} />
      <Box sx={nameSx}>{batch.name}</Box>
      <SegmentedBar counts={batch.counts} height={6} sx={{ ...BAR_SX, minWidth: 96 }} />
      <Box sx={{ ...metaSx, ...COUNT_SX, justifySelf: 'end', color: 'text.primary' }}>
        {done}/{total} done
      </Box>
    </Box>
  )
}

const SingleRow = ({ row, now, onReveal }) => {
  const theme = useTheme()
  const label = rowSampleLabel(row)
  const started = new Date(row.created_at).getTime()
  const hasStart = Number.isFinite(started)
  const step = currentStepOf(row)
  return (
    <ButtonBase
      component="div"
      data-row
      aria-label={`${label}, ${step}. Show in Analysis history`}
      onClick={() => onReveal(row)}
      sx={rowSx(theme, { interactive: true })}
    >
      <KindIcon kind={isSomaticRow(row) ? 'somatic' : 'germline'} size={16} />
      <FileNameText name={label} variant="plain" maxLines={1} />
      <Box sx={{ ...metaSx, ...FULL_ONLY_SX }}>{shortRowType(row)}</Box>
      <Box sx={{ ...FULL_ONLY_SX, alignItems: 'center', minWidth: 0 }}>
        <StepLanes row={row} />
      </Box>
      <Box sx={{ ...metaSx, color: 'text.primary', maxWidth: 140 }}>{step}</Box>
      {hasStart ? (
        <Tooltip
          title={`Started ${formatWhen(started, now)}, running for ${
            formatDuration(now - started) || 'under a minute'
          }`}
          describeChild
        >
          <Box component="span" sx={{ ...metaSx, justifySelf: 'end' }}>
            {formatRunning(now - started)}
          </Box>
        </Tooltip>
      ) : (
        <Box component="span" />
      )}
    </ButtonBase>
  )
}

const InProgressCard = ({ onReveal, onShowHistory }) => {
  const theme = useTheme()
  const { batches, singles, anyBatch, lastFinished, isLoading, isError, refetch } = useInProgress()
  useMinuteTick(singles.length > 0)
  const now = Date.now()
  const { rows, moreBatches, moreSingles } = inProgressRows({ batches, singles })

  let body
  if (isError) {
    body = (
      <CardMessage
        title="Could not load running analyses."
        action={
          <ResultButton compact onClick={() => refetch()}>
            Try again
          </ResultButton>
        }
      />
    )
  } else if (isLoading) {
    body = <RowSkeletons count={3} />
  } else if (!rows.length) {
    body = <CardMessage title="Nothing running right now." detail={lastFinishedText(lastFinished, now)} />
  } else {
    body = (
      <Box sx={{ ...listSx(theme), ...COLUMNS_SX }}>
        {rows.map((item) =>
          item.type === 'batch' ? (
            <BatchRow key={item.key} batch={item.batch} />
          ) : (
            <SingleRow key={item.key} row={item.row} now={now} onReveal={onReveal} />
          ),
        )}
      </Box>
    )
  }

  const linkSx = { fontSize: 13, fontWeight: 500 }
  // Batches that did not fit are on All batches, analyses in Analysis History.
  const footer =
    !isLoading && !isError && (anyBatch || moreSingles > 0) ? (
      <>
        {anyBatch && (
          <Link component={RouterLink} to={PATH_DASHBOARD.general.batches} underline="hover" sx={linkSx}>
            {moreBatches > 0 ? `+${pluralise(moreBatches, 'more batch', 'more batches')}` : 'All batches'}
          </Link>
        )}
        {moreSingles > 0 && (
          <ButtonBase
            onClick={onShowHistory}
            disableRipple
            sx={{
              font: 'inherit',
              ...linkSx,
              color: 'text.secondary',
              borderRadius: 0.5,
              '&:hover': { color: 'text.primary', textDecoration: 'underline' },
              '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
          >
            +{moreSingles} more in Analysis history
          </ButtonBase>
        )}
      </>
    ) : null

  return (
    <HomeCard
      title="In progress"
      titleId="home-progress-title"
      count={!isLoading && !isError ? inProgressCountText({ batches, singles }) : ''}
      footer={footer}
      data-testid="progress-card"
    >
      {body}
    </HomeCard>
  )
}

export default InProgressCard
